/** Simplified version of: https://github.com/samuelmeuli/react-magnifier
 * Removed touch events, circular magnifier & overflow option
 */

import React, { PureComponent } from 'react';
import styles from './magnifier-component.module.scss';

type MagnifierProps = {
  // Image
  src: string;

  // Zoom image
  zoomImgSrc?: string;
  zoomFactor?: number;

  // Magnifying glass
  mgWidth?: number;
  mgHeight?: number;

  mgMouseOffsetX?: number;
  mgMouseOffsetY?: number;
};

interface State {
  showZoom: boolean;

  // Magnifying glass offset
  mgOffsetX: number;
  mgOffsetY: number;

  // Mouse position relative to image
  relX: number;
  relY: number;
}

export class Magnifier extends PureComponent<MagnifierProps, State> {
  img: HTMLElement | undefined;

  imgBounds: DOMRect | ClientRect | undefined;

  calcImgBoundsDebounced: () => void;

  static defaultProps = {
    // Zoom image
    zoomImgSrc: '',
    zoomFactor: 1.5,

    // Magnifying glass
    mgWidth: 200,
    mgHeight: 200,
    mgMouseOffsetX: 0,
    mgMouseOffsetY: 0,
  };

  state: Readonly<State> = {
    showZoom: false,
    mgOffsetX: 0,
    mgOffsetY: 0,
    relX: 0,
    relY: 0,
  };

  constructor(props: MagnifierProps) {
    super(props);

    this.calcImgBoundsDebounced = this.calcImgBounds;
  }

  componentDidMount = (): void => {
    // Add mouse event listeners to image element (assigned in render function)
    // `passive: false` prevents scrolling on touch move
    this.img?.addEventListener('mouseenter', this.onMouseEnter, { passive: false });
    this.img?.addEventListener('mousemove', this.onMouseMove, { passive: false });
    this.img?.addEventListener('mouseout', this.onMouseOut, { passive: false });

    // Re-calculate image bounds on window resize
    window.addEventListener('resize', this.calcImgBoundsDebounced);
    // Re-calculate image bounds on scroll (useCapture: catch scroll events in entire DOM)
    window.addEventListener('scroll', this.calcImgBoundsDebounced, true);
  };

  componentWillUnmount = (): void => {
    // Remove all event listeners
    this.img?.removeEventListener('mouseenter', this.onMouseEnter);
    this.img?.removeEventListener('mousemove', this.onMouseMove);
    this.img?.removeEventListener('mouseout', this.onMouseOut);
    window.removeEventListener('resize', this.calcImgBoundsDebounced);
    window.removeEventListener('scroll', this.calcImgBoundsDebounced, true);
  };

  onMouseEnter = (): void => {
    this.calcImgBounds();
  };

  onMouseMove = (e: MouseEvent): void => {
    const { mgMouseOffsetX, mgMouseOffsetY } = this.props;

    if (this.imgBounds) {
      const target = e.target as HTMLElement;
      const relX = (e.clientX - this.imgBounds.left) / target.clientWidth;
      const relY = (e.clientY - this.imgBounds.top) / target.clientHeight;

      this.setState({
        mgOffsetX: mgMouseOffsetX ?? 0,
        mgOffsetY: mgMouseOffsetY ?? 0,
        relX,
        relY,
        showZoom: true,
      });
    }
  };

  onMouseOut = (): void => {
    this.setState({
      showZoom: false,
    });
  };

  calcImgBounds = (): void => {
    if (this.img) {
      this.imgBounds = this.img.getBoundingClientRect();
    }
  };

  render = (): React.ReactElement => {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const { src, zoomImgSrc, zoomFactor, mgHeight, mgWidth, mgMouseOffsetX, mgMouseOffsetY, ...otherProps } = this.props;
    /* eslint-enable @typescript-eslint/no-unused-vars */
    const { mgOffsetX, mgOffsetY, relX, relY, showZoom } = this.state;

    // Show/hide magnifying glass (opacity needed for transition)
    let mgClasses = styles['magnifying-glass'];
    if (showZoom) {
      mgClasses += ` ${styles.visible}`;
    }

    const magnifierWidth = mgWidth ?? 200;
    const magnifierHeight = mgHeight ?? 200;
    const zoomBy = zoomFactor ?? 1.5;

    return (
      <div className={styles.magnifier} data-verbolia-id='magnifier-component-magnifier'>
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img
          className={styles['magnifier-image']}
          src={src}
          {...otherProps}
          onLoad={(): void => {
            this.calcImgBounds();
          }}
          ref={(img: HTMLImageElement): void => {
            this.img = img;
          }}
          data-verbolia-id='magnifier-component-magnifier-image'
        />
        {this.imgBounds && (
          <div
            className={mgClasses}
            style={{
              width: magnifierWidth,
              height: magnifierHeight,
              left: `calc(${relX * 100}% - ${magnifierWidth / 2}px + ${mgOffsetX}px)`,
              top: `calc(${relY * 100}% - ${magnifierWidth / 2}px + ${mgOffsetY}px)`,
              backgroundImage: `url("${zoomImgSrc || src}")`,
              backgroundPosition: `calc(${relX * 100}% + ${magnifierWidth / 2}px - ${relX * magnifierWidth}px) calc(${relY * 100}% + ${
                magnifierHeight / 2
              }px - ${relY * magnifierWidth}px)`,
              backgroundSize: `${zoomBy * this.imgBounds.width}% ${zoomBy * this.imgBounds.height}%`,
            }}
          />
        )}
      </div>
    );
  };
}

export default Magnifier;
